/**
 * SessionsSidebar — iter-121.29 cosmetic redesign.
 *
 * Same conversation list semantics (open / rename / star / delete /
 * delete-all) — just a premium left-rail layout per the design comp:
 *
 *   ┌─────────────────────────────┐
 *   │  + New conversation         │  ← solid primary CTA
 *   ├─────────────────────────────┤
 *   │  🔍  Search conversations    │  ← search input
 *   │                       ▾     │
 *   ├─────────────────────────────┤
 *   │  Recent conversations       │  ← muted eyebrow
 *   │  💬 Revenue trend …  ⋯      │  ← conversation card w/ 3-dot menu
 *   │     10:30 AM                │
 *   │  💬 Top 5 customers  ⋯      │
 *   │     Yesterday               │
 *   │  …                          │
 *   ├─────────────────────────────┤
 *   │  View all conversations  →  │
 *   └─────────────────────────────┘
 *
 * Star is a client-side bookmark (localStorage-keyed by session id) so
 * the redesign stays purely cosmetic — zero backend changes. The filter
 * funnel toggles a Starred-only view.
 *
 * Delete-all preserved as a destructive item in the filter dropdown so
 * power users keep the workflow without polluting the sidebar with a
 * secondary CTA.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
    Plus, MessageSquare, Trash2, Pencil, Search, SlidersHorizontal,
    Star, MoreHorizontal, ChevronRight, Folder,
} from "lucide-react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const LEGACY_STAR_KEY = "analyzio_senseai_starred_v1";
const MIGRATION_FLAG_KEY = "analyzio_senseai_star_migrated_v1";

/** Relative time formatter — matches the design comp's "10:30 AM /
 * Yesterday / May 20" pattern. */
function formatSessionTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (isYesterday) return "Yesterday";
    // Older this year — show "Mon DD"; cross-year — show "MMM DD YYYY"
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString([], sameYear
        ? { month: "short", day: "numeric" }
        : { month: "short", day: "numeric", year: "numeric" });
}

/* -------------------------------------------------------------------------- */
/*  SessionCard — one conversation row                                        */
/* -------------------------------------------------------------------------- */
function SessionCard({
    session, isActive, isStarred, onOpen, onRename, onStar, onDelete,
}) {
    return (
        <div
            className={`group relative rounded-md transition-all cursor-pointer mb-1 ${isActive
                ? "bg-primary/5 border border-primary/20"
                : "border border-transparent hover:bg-secondary/40 hover:border-border"
                }`}
            onClick={onOpen}
            data-testid={`sense-ai-session-${session.id}`}>
            <div className="flex items-start gap-2.5 px-3 py-2.5">
                <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${isActive ? "bg-primary/10" : "bg-secondary"
                    }`}>
                    <MessageSquare className={`h-3.5 w-3.5 ${isActive ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0 pr-1">
                    <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium truncate text-foreground" title={session.title}>
                            {session.title || "Untitled conversation"}
                        </span>
                    </div>
                    <div className="text-[10.5px] text-muted-foreground mt-0.5">
                        {formatSessionTime(session.updated_at || session.created_at)}
                    </div>
                </div>
                {/* iter-121.30 — Quick-star: always-visible toggle so users can
                    bookmark a conversation without opening the 3-dot menu.
                    Filled amber when starred, outline otherwise. */}
                <button
                    onClick={(e) => { e.stopPropagation(); onStar(); }}
                    className={`h-6 w-6 inline-flex items-center justify-center rounded-sm transition-colors shrink-0 ${isStarred
                        ? "text-amber-500 hover:bg-amber-500/10"
                        : "text-muted-foreground/60 hover:text-amber-500 hover:bg-secondary"
                        }`}
                    title={isStarred ? "Unstar" : "Star conversation"}
                    data-testid={`sense-ai-quick-star-${session.id}`}>
                    <Star
                        className={`h-3.5 w-3.5 ${isStarred ? "fill-amber-500" : ""}`}
                        strokeWidth={1.75}
                    />
                </button>
                {/* iter-121.30 — 3-dot menu now always visible (was hover-only
                    per design feedback). The icon is muted; only becomes
                    primary when the dropdown is open. */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            onClick={(e) => e.stopPropagation()}
                            className="h-6 w-6 inline-flex items-center justify-center rounded-sm text-muted-foreground/60 hover:text-foreground hover:bg-secondary data-[state=open]:bg-secondary data-[state=open]:text-foreground transition-colors shrink-0"
                            title="Conversation actions"
                            data-testid={`sense-ai-row-menu-${session.id}`}>
                            <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); onOpen(); }}
                            data-testid={`sense-ai-action-open-${session.id}`}>
                            <MessageSquare className="h-3.5 w-3.5 mr-2" /> Open conversation
                        </DropdownMenuItem>
                        {onRename && (
                            <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); onRename(); }}
                                data-testid={`sense-ai-action-rename-${session.id}`}>
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); onStar(); }}
                            data-testid={`sense-ai-action-star-${session.id}`}>
                            <Star className={`h-3.5 w-3.5 mr-2 ${isStarred ? "fill-amber-500 text-amber-500" : ""}`} />
                            {isStarred ? "Unstar" : "Star conversation"}
                        </DropdownMenuItem>
                        {onDelete && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                    className="text-destructive focus:text-destructive"
                                    data-testid={`sense-ai-action-delete-${session.id}`}>
                                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete conversation
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  SessionsSidebar                                                           */
/* -------------------------------------------------------------------------- */
export default function SessionsSidebar({ ctx }) {
    const { sessions, setSessions, active, openSession, newSession, del, delAll, hasWrite } = ctx;
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all"); // "all" | "starred"
    const [showAll, setShowAll] = useState(false);
    const [isRenameOpen, setIsRenameOpen] = useState(false);
    const [renamingSession, setRenamingSession] = useState(null);
    const [renameTitle, setRenameTitle] = useState("");


    // iter-121.31 — Star is now BACKEND-persisted on `chat_sessions.starred`
    // so the same starred set surfaces on every device the user signs in
    // from. We derive the Set from the sessions list (single source of
    // truth) and persist toggles via PATCH /sessions/<id> { starred }.
    const stars = useMemo(
        () => new Set((sessions || []).filter((s) => s.starred).map((s) => s.id)),
        [sessions],
    );

    // One-shot migration: if legacy localStorage stars exist, push them
    // to the backend and clear the local cache so subsequent sessions
    // read straight from the DB. Runs at most once per browser per user.
    useEffect(() => {
        if (!sessions || sessions.length === 0) return;
        let migrated = false;
        try { migrated = localStorage.getItem(MIGRATION_FLAG_KEY) === "1"; } catch { /* swallow */ }
        if (migrated) return;
        let legacyIds = [];
        try { legacyIds = JSON.parse(localStorage.getItem(LEGACY_STAR_KEY) || "[]"); }
        catch { legacyIds = []; }
        if (!Array.isArray(legacyIds) || legacyIds.length === 0) {
            // Nothing to migrate — still mark as done so we don't recheck.
            try { localStorage.setItem(MIGRATION_FLAG_KEY, "1"); } catch { /* swallow */ }
            return;
        }
        // Push each legacy star that's NOT yet starred on the backend.
        const ownedIds = new Set((sessions || []).map((s) => s.id));
        const toPatch = legacyIds.filter((id) => ownedIds.has(id) && !stars.has(id));
        (async () => {
            try {
                await Promise.all(toPatch.map((id) =>
                    api.patch(`/sense-ai/sessions/${id}`, { starred: true }).catch(() => null),
                ));
                // Reflect migrated stars in the local sessions list so the UI
                // updates without a full reload.
                if (toPatch.length > 0) {
                    setSessions((prev) => (prev || []).map((s) =>
                        toPatch.includes(s.id) ? { ...s, starred: true } : s,
                    ));
                }
            } finally {
                try {
                    localStorage.setItem(MIGRATION_FLAG_KEY, "1");
                    localStorage.removeItem(LEGACY_STAR_KEY);
                } catch { /* swallow */ }
            }
        })();
        // eslint-disable-next-line
    }, [sessions]);

    const toggleStar = async (s) => {
        const wasStarred = stars.has(s.id);
        const nextVal = !wasStarred;
        // Optimistic UI — flip locally first so the click feels instant.
        setSessions((prev) => (prev || []).map((x) =>
            x.id === s.id ? { ...x, starred: nextVal } : x,
        ));
        try {
            await api.patch(`/sense-ai/sessions/${s.id}`, { starred: nextVal });
            toast.success(nextVal ? "Starred" : "Unstarred");
        } catch (err) {
            // Revert on failure so the UI stays consistent with the DB.
            setSessions((prev) => (prev || []).map((x) =>
                x.id === s.id ? { ...x, starred: wasStarred } : x,
            ));
            toast.error(err?.response?.data?.detail || "Failed to update star");
        }
    };

    const handleRenameSubmit = async () => {
        if (!renamingSession || !renameTitle.trim() || renameTitle.trim() === renamingSession.title) {
            setIsRenameOpen(false);
            return;
        }
        try {
            const next = renameTitle.trim();
            await api.patch(`/sense-ai/sessions/${renamingSession.id}`, { title: next });
            setSessions((prev) => prev.map((x) => x.id === renamingSession.id ? { ...x, title: next } : x));
            if (active?.id === renamingSession.id) ctx.setActive((a) => ({ ...a, title: next }));

            toast.success("Renamed");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Rename failed");
        } finally {
            setIsRenameOpen(false);
        }
    };

    const triggerRename = (s) => {
        setRenamingSession(s);
        setRenameTitle(s.title || "");
        setIsRenameOpen(true);
    };

    const filtered = useMemo(() => {
        let arr = sessions || [];
        if (filter === "starred") arr = arr.filter((s) => stars.has(s.id));
        const term = q.trim().toLowerCase();
        if (term) arr = arr.filter((s) => (s.title || "").toLowerCase().includes(term));
        return arr;
    }, [sessions, filter, q, stars]);

    const previewLimit = 6;
    const preview = showAll ? filtered : filtered.slice(0, previewLimit);
    const overflow = filtered.length - preview.length;

    return (
        <aside className="w-[300px] shrink-0 border-r border-border flex flex-col bg-card/40" data-testid="sense-ai-sidebar">
            {/* New conversation CTA */}
            <div className="p-4">
                <Button
                    onClick={newSession}
                    disabled={!hasWrite}
                    className="w-full h-10 rounded-md shadow-sm gap-1.5"
                    data-testid="sense-ai-new-session">
                    <Plus className="h-4 w-4" /> New conversation
                </Button>
            </div>

            {/* Search + filter funnel */}
            <div className="px-4 pb-3 flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search conversations"
                        className="w-full h-9 pl-9 pr-3 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-shadow"
                        data-testid="sense-ai-search"
                    />
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className={`h-9 w-9 inline-flex items-center justify-center rounded-md border transition-colors ${filter !== "all"
                                ? "bg-primary/10 border-primary/30 text-primary"
                                : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                                }`}
                            title="Filter conversations"
                            data-testid="sense-ai-filter-toggle">
                            <SlidersHorizontal className="h-3.5 w-3.5" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            Show
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                            onClick={() => setFilter("all")}
                            className={filter === "all" ? "font-medium" : ""}
                            data-testid="sense-ai-filter-all">
                            <MessageSquare className="h-3.5 w-3.5 mr-2" /> All conversations
                            {filter === "all" && <span className="ml-auto text-primary">✓</span>}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => setFilter("starred")}
                            className={filter === "starred" ? "font-medium" : ""}
                            data-testid="sense-ai-filter-starred">
                            <Star className="h-3.5 w-3.5 mr-2" /> Starred only
                            {filter === "starred" && <span className="ml-auto text-primary">✓</span>}
                        </DropdownMenuItem>
                        {sessions.length > 0 && hasWrite && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={delAll}
                                    className="text-destructive focus:text-destructive"
                                    data-testid="sense-ai-delete-all">
                                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete all ({sessions.length})
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Recent conversations header */}
            <div className="px-4 pb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-testid="sense-ai-sidebar-eyebrow">
                    {filter === "starred" ? "Starred conversations" : "Recent conversations"}
                </div>
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto scrollbar-thin px-3">
                {filtered.length === 0 ? (
                    <div className="border border-dashed border-border rounded-md py-10 px-4 text-center mx-1" data-testid="sense-ai-empty-list">
                        {filter === "starred" ? (
                            <>
                                <Star className="h-6 w-6 mx-auto mb-2 text-muted-foreground/40" strokeWidth={1.5} />
                                <div className="text-xs text-foreground/80">No starred conversations</div>
                                <div className="text-[10px] text-muted-foreground mt-1">
                                    Star a conversation via its ⋯ menu to pin it here.
                                </div>
                            </>
                        ) : q.trim() ? (
                            <>
                                <Search className="h-6 w-6 mx-auto mb-2 text-muted-foreground/40" strokeWidth={1.5} />
                                <div className="text-xs text-foreground/80">No matches</div>
                                <div className="text-[10px] text-muted-foreground mt-1">
                                    Try a different keyword or clear the search.
                                </div>
                            </>
                        ) : (
                            <>
                                <Folder className="h-6 w-6 mx-auto mb-2 text-muted-foreground/40" strokeWidth={1.5} />
                                <div className="text-xs text-foreground/80">No conversations yet</div>
                                <div className="text-[10px] text-muted-foreground mt-1">
                                    Ask Sense AI anything to start your first conversation.
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    preview.map((s) => (
                        <SessionCard
                            key={s.id}
                            session={s}
                            isActive={active?.id === s.id}
                            isStarred={stars.has(s.id)}
                            onOpen={() => openSession(s)}
                            onRename={hasWrite ? () => triggerRename(s) : undefined}
                            onStar={() => toggleStar(s)}
                            onDelete={hasWrite ? () => del(s) : undefined}
                        />
                    ))
                )}
            </div>
            
            <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
                <DialogContent className="sm:max-w-[420px] bg-background border border-border shadow-lg rounded-lg p-6">
                    <DialogHeader className="pb-3 border-b border-border">
                        <DialogTitle className="text-base font-semibold text-foreground">Rename conversation</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Input
                            type="text"
                            value={renameTitle}
                            onChange={(e) => setRenameTitle(e.target.value)}
                            placeholder="Enter conversation title..."
                            className="w-full h-10 px-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleRenameSubmit();
                                }
                            }}
                        />
                    </div>
                    <DialogFooter className="pt-3 border-t border-border gap-2 flex justify-end">
                        <Button variant="outline" onClick={() => setIsRenameOpen(false)} className="h-9 px-4 text-xs">
                            Cancel
                        </Button>
                        <Button onClick={handleRenameSubmit} disabled={!renameTitle.trim() || renameTitle.trim() === renamingSession?.title} className="h-9 px-4 text-xs">
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* View all / Show less toggle. The 'View all' affordance appears
                whenever there's overflow; once expanded, the same control
                flips to 'Show less' so users can collapse the list back to
                the preview size. */}
            {(overflow > 0 || (showAll && filtered.length > previewLimit)) && (
                <div className="px-4 py-3 border-t border-border">
                    <button
                        onClick={() => setShowAll((v) => !v)}
                        className="w-full flex items-center justify-between text-xs text-foreground hover:text-primary transition-colors px-3 py-2 rounded-md hover:bg-secondary/40"
                        data-testid="sense-ai-view-all">
                        <span>
                            {showAll
                                ? "Show less conversations"
                                : `View all conversations${overflow > 0 ? ` (${filtered.length})` : ""}`}
                        </span>
                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-90" : ""}`} />
                    </button>
                </div>
            )}
        </aside>
    );
}
